import type { Metadata } from 'next';
import { config } from '@/config/config';

export const EVENT_ROOT_SEGMENTS = ['site', 'event'] as const;
export type EventRootSegment = (typeof EVENT_ROOT_SEGMENTS)[number];

export function isEventRootSegment(value: string): value is EventRootSegment {
  return (EVENT_ROOT_SEGMENTS as readonly string[]).includes(value);
}

// Sanitizes an untrusted segment value (query param, storage, gateway callback…):
// anything that is not exactly 'site' | 'event' falls back to 'site'.
export function sanitizeEventRootSegment(
  value: string | null | undefined,
  fallback: EventRootSegment = 'site',
): EventRootSegment {
  return value != null && isEventRootSegment(value) ? value : fallback;
}

// Reads the event tree segment ('site' | 'event') from the current pathname
// (/[lang]/site/... or /[lang]/event/...). Falls back when the pathname does not
// belong to either tree (e.g. recovery flows landing outside the event routes).
export function getEventRootSegment(
  pathname: string,
  fallback: EventRootSegment = 'site',
): EventRootSegment {
  return sanitizeEventRootSegment(pathname.split('/').filter(Boolean)[1], fallback);
}

// Semántica de los dos árboles: 'event' es la vista pública SEO (estilos generales del
// sitio, solo eventos published); 'site' es el microsite del organizador. Usar este helper
// en lugar de comparar strings sueltos — un único punto de verdad sobre qué es cada árbol.
export function isPublicEventView(view: string): boolean {
  return view === 'event';
}

// Builds the href to the event "root" page (where the registration flow was entered from)
// by reading the route segment from the current pathname. The shared tree lives under
// /[lang]/[view]/[eventAlias]/... where view is 'site' (microsite) or 'event' (public
// SEO-indexable tree) — "back to event" must mirror whichever segment is in use.
export function getEventRootHref(
  pathname: string,
  lang: string,
  eventAlias: string,
  fallback: EventRootSegment = 'site',
): string {
  return `/${lang}/${getEventRootSegment(pathname, fallback)}/${eventAlias}`;
}

// Route-tree rule for `getEventByAlias(alias, countryId, calledFromChatbot)`:
// the 'site' tree is the organizer microsite view and must work with private/draft
// events (preview, direct links), so it may see unpublished events; the 'event'
// tree is the public SEO-indexable one — the BE enforces status="published".
// ALWAYS derive the flag from the route's `view` param via this helper; never pass
// a `true`/`false` literal from pages living under the shared [view] tree.
export function allowsUnpublishedEvents(view: string): boolean {
  return view === 'site';
}

/** URL pública absoluta de una página del árbol de evento. `subPath` vacío = portada. */
export function eventTreeUrl(
  lang: string,
  view: string,
  eventAlias: string,
  subPath = '',
): string {
  // Sin barra final cuando no hay sub-ruta: `/es/site/{alias}/` redirige (308) a
  // `/es/site/{alias}`, y una canónica que apunta a un redirect es una canónica que se descarta.
  const suffix = subPath ? `/${subPath}` : '';
  return `${config.app.url}/${lang}/${sanitizeEventRootSegment(view)}/${eventAlias}${suffix}`;
}

/**
 * Canónica auto-referencial del segmento que se está sirviendo ('site' o 'event').
 *
 * Next.js NO emite una canónica auto-referencial por defecto: una página que no declara
 * `alternates.canonical` sale SIN etiqueta. En este árbol eso es caro —/site/* y /event/*
 * sirven el mismo HTML— así que esta es la señal mínima que toda página debe emitir, y el
 * fallback de `eventCanonicalAlternates` (ver @/lib/event-canonical) cuando no se puede
 * consolidar hacia /event/*.
 *
 * Es también la canónica DEFINITIVA de la portada: `/site/{alias}` (microsite del organizador)
 * y `/event/{alias}` (ficha pública) son páginas distintas, no dos vistas del mismo contenido.
 */
export function eventTreeSelfCanonical(
  lang: string,
  view: string,
  eventAlias: string,
  subPath = '',
): Metadata['alternates'] {
  return { canonical: eventTreeUrl(lang, view, eventAlias, subPath) };
}
